<!--
    * Vue2js modal component.
    * variables:
    *    modalOptions.show    Display / hide modal
    * Example usage:
    * <ls-modal :options="modalOptions" @onModalClose="modalOptions.show=false">
    *     <div> modal content </div>
    * </ls-modal>
    *
    * to open:
    * <button @click="modalOptions.show=true">show modal</button>
-->
<template>
    <transition name="ls-fade" mode="out-in" appear>
        <div v-if="options.show" class="ls-modal__overlay" @click="close()">
            <div class="ls-modal__panel" @click.stop>
                <a v-if="!$slots.header" class="ls-modal__close-button" @click="close()">&times;</a>

                <div v-if="$slots.header" class="ls-modal__header">
                    <a class="ls-modal__close-button" @click="close()">&times;</a>
                    <slot name="header"></slot>
                </div>

                <div class="ls-modal__content" :class="{ 'disable-scroll': disableContentScroll }">
                    <slot></slot>
                    <div v-if="$slots.button" class="ls-modal__button-container">
                        <slot name="button"></slot>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    props: {
        options: { type: Object },
        disableContentScroll: { type: Boolean },
    },
    mounted() {
        window.addEventListener('keyup', this.detectEscape);
    },
    beforeDestroy() {
        window.removeEventListener('keyup', this.detectEscape);
    },
    methods: {
        close() {
            this.$emit('onModalClose');
        },
        detectEscape(e) {
            const which = e.keyCode || e.which;
            if (this.options.show && which === 27) {
                this.$emit('onModalClose');
            }
        },
    },
};
</script>

<style lang="scss">
.ls-modal__overlay {
    // background overlay
    position: fixed;
    overflow: scroll;
    z-index: z-index(modal);
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100vw;
    min-height: 100vh;
    height: 100vh;
    padding-bottom: rem(0); // same as margin-bottom on .ls-modal__panel
    background-color: rgba(black, 0.65);

    // center the panel
    display: flex;
    align-items: flex-start;
    justify-content: center;
}

.ls-modal__close-button {
    cursor: pointer;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;

    width: rem(6);
    height: rem(6);
    line-height: rem(6);

    white-space: nowrap;
    text-align: center;

    color: palette(gray, mid);
    font-size: rem(4);
    font-weight: 900;

    &:hover {
        color: palette(black);
    }
}

.ls-modal__panel {
    position: relative; // for positioning the button-container
    margin-bottom: rem(0);
    background-color: palette(white);
    border-radius: rem(-5);
    border: 1px solid palette(gray);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);

    @include max-screen(breakpoint(xs, max)) {
        position: absolute;
        top: rem(0); // prevent modal from
        left: rem(0); // toucing edges of screen
        right: rem(0); // for mobile screens
        margin-bottom: 100px; // ensures bottom bars wont cover bottom of modal

        padding: rem(0) 0; // identical to padding on `.ls-panel`
    }
    @include min-screen(breakpoint(sm)) {
        flex: 1 1 auto;
        position: relative; // for positioning close-button
        max-width: 600px;
        min-width: 320px;
        margin-top: rem(4); // prevent modal from touching top edge of screen for non-mobile screens

        padding: rem(0) rem(-3); // identical to padding on `.ls-panel`
    }
}

.ls-modal__header {
    padding: 0 rem(5);
    padding-bottom: rem(0);
    margin-bottom: rem(0);
    border-bottom: 1px solid palette(gray, x-light);
}

.ls-modal__content {
    @include max-screen(breakpoint(xs, max)) {
        max-height: 60vh;
        overflow-y: scroll;
    }

    &.disable-scroll {
        @include max-screen(breakpoint(xs, max)) {
            max-height: initial;
            overflow-y: initial;
        }
    }
}

.ls-modal__button-container {
    margin-top: rem(0);
    // spacing for bottom border of button
    padding-bottom: 2px;
    padding-top: rem(0);
    border-top: 1px solid palette(gray, x-light);
}
</style>
